/**
 * @file    fieldMapping.ts
 * @scope   SHARED
 * @summary API ⇄ UI mapping for fields/filters: builds FieldsManager view options, maps fields and
 *          calculations to select options, and recovers deserialized filters against the real config.
 * @consumes DataSource/FieldsManager models, filterTypeResolver (candidate paths / attribute kind)
 * @produces Select-option mappers, view-option builders, `recoverEditModeFields` and helpers
 * @sideEffects None — pure mapping functions.
 * @notes WHY filter recovery is forgiving: at serialization the path suffix is derived from the
 *        field's TypeName + Responses, but at recovery we only have the stored path and don't know
 *        which rule "won", so we enumerate every plausible suffix (rating / id / flag / empty).
 */

import { PartitionModel } from 'models/Enterprise/DataSourceModel';
import {
  FieldModel,
  FieldFilter,
  FieldsManagerViewOptionsModel,
  SelectedUserFields,
} from 'models/Enterprise/FieldsManagerModel';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import { buildCandidatePaths, resolveAttributeKind } from '../filters/filterTypeResolver';

// CX.DiyQueryBuilder.Enums.CalculationType; the existing UI stores numeric type codes.
const calculationTypeCodes: Readonly<Record<string, string>> = {
  unknown: '0',
  mean: '1',
  percentage: '2',
  nps: '3',
  totalcount: '4',
  median: '6',
};

/**
 * Builds every candidate attributePath a stored filter could legitimately match against.
 *
 * Recovery must be forgiving: at serialization time the suffix is derived from the
 * field's TypeName + Responses, but at recovery time we only have the stored path
 * and we don't know which rule "won". So we enumerate every plausible suffix
 * (rating / id / flag / empty)
 */

/**
 * Re-matches a deserialized filter stub against the available filter fields.
 * Restores filterGuid, filterName, fieldTypeName, and attributeKind from the real config.
 */
const recoverFilter = (
  filter: FieldFilter,
  filtersOptions: AdvancedFilterFieldConfig[]
): FieldFilter => {
  if (filter.filterGuid) return filter;

  const match = filtersOptions.find((f) => buildCandidatePaths(f).includes(filter.attributePath));

  if (!match) return filter;

  const recoveredValues = filter.selectedValues.map((sv) => {
    const resp = match.responses?.find(
      (r) => String(r.id) === String(sv.value) || String(r.rating) === String(sv.value)
    );
    if (!resp) return sv;
    return {
      ...sv,
      label: resp.choice ?? sv.label,
      longText: resp.flag ? `Flag: ${resp.flag}` : resp.choice ?? sv.longText,
    };
  });

  return {
    ...filter,
    filterGuid: match.attributeGuid,
    filterName: match.displayName || match.shortText || match.columnName,
    fieldTypeName: match.typeName,
    subAttribute: match.subAttribute || filter.subAttribute,
    attributeKind: resolveAttributeKind(match),
    selectedValues: recoveredValues,
  };
};

/**
 * Recovers field and calculationType references that were stored as minimal stubs in edit mode.
 * Matches against the available options loaded from the backend to fill in the real objects.
 * Sets FromEditMode to false once recovery is complete.
 * @param userFields - The selectedUserFields with fromEditMode: true
 * @param viewOptions - The fully loaded FieldsManagerViewOptionsModel from the API
 * @returns A new SelectedUserFields with enriched field/calculationType and fromEditMode: false
 */
export const recoverEditModeFields = (
  userFields: SelectedUserFields,
  viewOptions: FieldsManagerViewOptionsModel
): SelectedUserFields => {
  const recoveredFields = userFields.fields.map((field) => {
    const matchedField =
      viewOptions.FieldsOptions.find((f) => f.fieldName === field.field.columnName) ?? field.field;
    const matchedCalcType =
      viewOptions.CalculationTypeOptions.find(
        (c) => String(c.type) === String(field.calculationType?.type)
      ) ?? field.calculationType;

    const recoveredFilters = field.filters?.map((f) =>
      recoverFilter(f, viewOptions.FiltersOptions)
    );

    return {
      ...field,
      field: matchedField,
      calculationType: matchedCalcType,
      ...(recoveredFilters ? { filters: recoveredFilters } : {}),
    };
  });

  // Partition-level filters are stored as stubs too, so they need the same re-matching.
  const recoveredPartitionFilters = userFields.partitionFilters?.map((f) =>
    recoverFilter(f, viewOptions.FiltersOptions)
  );

  return {
    ...userFields,
    fields: recoveredFields,
    ...(recoveredPartitionFilters ? { partitionFilters: recoveredPartitionFilters } : {}),
    fromEditMode: false,
  };
};

/**
 * Maps the columns array from the partition data API response to FieldModel objects.
 * @param columns - The columns array from the API response
 * @returns An array of FieldModel objects
 */
export const mapColumnsToFieldOptions = (columns: any[]): FieldModel[] => {
  if (!columns || columns.length === 0) return [];
  return columns.map((col) => ({
    fieldGuid: col.colName,
    fieldName: col.field,
    displayName: col.displayName,
  }));
};

/**
 * Helper function to build the fieldsManagerOptions object for a specific partition
 * @param managedPartitionGuid - The partition to build options for
 * @param availableFieldsManagerOptions - Array of available field manager options
 * @returns A FieldsManagerViewOptionsModel or undefined
 */
export const buildFieldsManagerOptions = (
  managedPartitionGuid: PartitionModel | null,
  availableFieldsManagerOptions: FieldsManagerViewOptionsModel[]
): FieldsManagerViewOptionsModel | undefined => {
  if (!managedPartitionGuid) return undefined;

  const matchingOption = availableFieldsManagerOptions.find(
    (opt: any) => opt.dataCollectionGuid === managedPartitionGuid.value
  );

  return {
    parentPartition: managedPartitionGuid,
    CalculationTypeOptions: matchingOption?.CalculationTypeOptions || [],
    FieldsOptions: matchingOption?.FieldsOptions || [],
    FiltersOptions: matchingOption?.FiltersOptions || [],
  };
};

/**
 * Builds a FieldsManagerViewOptionsModel from a raw API response payload.
 * @param chartData - The GuardRails config (camelCase)
 * @param selectedPartition - The partition the data belongs to
 * @returns A FieldsManagerViewOptionsModel
 */
export const buildFieldsManagerViewOptions = (
  chartData: any,
  selectedPartition: PartitionModel
): FieldsManagerViewOptionsModel => {
  const fieldOptions: any[] = (chartData?.fields ?? []).map((field: any) => ({
    ...field,
    fieldName: field.fieldName ?? field.columnName,
    displayName: field.shortText ?? field.fieldName,
    responses: field.responses ?? [],
  }));

  const calculationOptions = (chartData?.customCalculations ?? []).map(
    (calculation: { type?: string | number }) => ({
      ...calculation,
      type: calculationTypeCodes[String(calculation.type).toLowerCase()] ?? calculation.type,
    })
  );

  return {
    parentPartition: selectedPartition,
    CalculationTypeOptions: calculationOptions,
    FieldsOptions: fieldOptions,
    FiltersOptions: chartData?.filters?.advancedFilters ?? [],
  };
};

/**
 * Maps FieldsOptions to { value, label } entries for a Select component.
 * @param fieldsOptions - The FieldsOptions array from FieldsManagerViewOptionsModel
 * @returns An array of select options
 */
export const mapFieldsToSelectOptions = (
  fieldsOptions: any[]
): { value: string; label: string }[] =>
  fieldsOptions.map((field: any) => ({
    value: field.fieldGuid,
    label: field.displayName || field.fieldName,
  }));

/**
 * Maps CalculationTypeOptions to { value, label } entries for a Select component.
 * Temporarily filters out hidden calculation types (see HIDDEN_CALCULATION_TYPES constant).
 * @param calculationOptions - The CalculationTypeOptions array from FieldsManagerViewOptionsModel
 * @returns An array of select options
 */
export const mapCalculationsToSelectOptions = (
  calculationOptions: any[]
): { value: string; label: string }[] =>
  calculationOptions.map((type: any) => ({
    value: type.type ?? type.displayName,
    label: type.displayName,
  }));

/**
 * Maps FiltersOptions to { value, label } entries for a Select component.
 * @param filtersOptions - The FiltersOptions array from FieldsManagerViewOptionsModel
 * @returns An array of select options
 */
export const mapFiltersToSelectOptions = (
  filtersOptions: any[]
): { value: string; label: string }[] =>
  filtersOptions.map((filter: any) => ({
    value: filter.filterGuid,
    label: filter.filterName,
  }));

/**
 * Appends a partitionGuid query parameter to an endpoint URL.
 * @param endpoint - The base endpoint URL
 * @param partitionGuid - The partition GUID to append
 * @returns The full URL with the partitionGuid param
 */
export const buildEndpointWithPartition = (endpoint: string, partitionGuid: string): string => {
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}partitionGuid=${partitionGuid}`;
};

/**
 * Helper function to get the selected user fields for a specific partition
 * @param managedPartitionGuid - The partition to get fields for
 * @param selectedEnterpriseFields - Array of selected enterprise fields
 * @returns A SelectedUserFields object
 */
export const getSelectedUserFields = (
  managedPartitionGuid: PartitionModel | null,
  selectedEnterpriseFields: SelectedUserFields[]
): SelectedUserFields => {
  if (managedPartitionGuid && selectedEnterpriseFields && selectedEnterpriseFields.length > 0) {
    return (
      selectedEnterpriseFields.find(
        (fields: any) => fields.partitionGuid === managedPartitionGuid.value
      ) || { partitionGuid: managedPartitionGuid.value, fields: [] }
    );
  }
  return { partitionGuid: managedPartitionGuid?.value || '', fields: [] };
};
